import { Platform } from 'react-native';
import { Packer } from 'docx';

import { ResumeDocument } from '../../types';
import { track } from '../analytics';
import { maybeShowInterstitial } from '../interstitial';
import { templateRegistry } from '.';

export interface ExportLabels {
  contact: string;
  summary: string;
  experience: string;
  education: string;
  skills: string;
  languages: string;
  links: string;
  present: string;
}

const FILENAME_SUFFIX = '_remote_work';

const safeFileName = (name: string, ext: string) => {
  const cleaned =
    (name || 'resume')
      // Strip path separators and control characters from user-provided names.
      // eslint-disable-next-line no-control-regex -- the control range is intentional
      .replace(/[/\\:*?"<>|\x00-\x1F]+/g, '_')
      .trim()
      .replace(/^[.\s]+|[.\s]+$/g, '')
      .slice(0, 60) || 'resume';
  return `${cleaned}${FILENAME_SUFFIX}.${ext}`;
};

const PDF_MIME = 'application/pdf';
const DOCX_MIME =
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document';

export async function exportToPdf(
  resume: ResumeDocument,
  labels: ExportLabels,
  hideAds = false
) {
  if (Platform.OS === 'web') return;

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const Print = require('expo-print') as typeof import('expo-print');
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const FileSystem = require('expo-file-system') as typeof import('expo-file-system');
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const Sharing = require('expo-sharing') as typeof import('expo-sharing');

  const html = templateRegistry[resume.template].toHtml(
    resume,
    labels,
    resume.personalization
  );
  const { uri } = await Print.printToFileAsync({ html });

  // expo-print drops the file at a randomized temp path. Move it into the
  // cache directory under the user-facing name so the share sheet (and any
  // downstream "Save to Files" action) uses CVName_remote_work.pdf.
  const desiredName = safeFileName(resume.name, 'pdf');
  const src = new FileSystem.File(uri);
  const dest = new FileSystem.File(FileSystem.Paths.cache, desiredName);
  try {
    dest.delete();
  } catch {
    /* not there yet — ignore */
  }
  src.move(dest);

  // The file is ready — this is the "task completed" moment. Show the
  // interstitial here (before the OS share sheet) so it never preempts a
  // user-visible export operation, and so the AdMob frequency cap is the
  // single source of truth.
  await maybeShowInterstitial(hideAds);

  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(dest.uri, {
      mimeType: PDF_MIME,
      dialogTitle: resume.name,
      UTI: 'com.adobe.pdf',
    });
  }

  track('resume_export', { format: 'pdf', template: resume.template });
}

export async function exportToDocx(
  resume: ResumeDocument,
  labels: ExportLabels,
  hideAds = false
) {
  if (Platform.OS === 'web') return;

  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const FileSystem = require('expo-file-system') as typeof import('expo-file-system');
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const Sharing = require('expo-sharing') as typeof import('expo-sharing');

  const doc = templateRegistry[resume.template].toDocx(
    resume,
    labels,
    resume.personalization
  );
  const base64 = await Packer.toBase64String(doc);

  const file = new FileSystem.File(
    FileSystem.Paths.cache,
    safeFileName(resume.name, 'docx')
  );
  try {
    file.delete();
  } catch {
    /* not there yet — ignore */
  }
  file.create({ overwrite: true });
  file.write(base64, { encoding: 'base64' });

  await maybeShowInterstitial(hideAds);

  if (await Sharing.isAvailableAsync()) {
    await Sharing.shareAsync(file.uri, {
      mimeType: DOCX_MIME,
      dialogTitle: resume.name,
      UTI: 'org.openxmlformats.wordprocessingml.document',
    });
  }

  track('resume_export', { format: 'docx', template: resume.template });
}

export default { exportToPdf, exportToDocx };
